import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Target, Users, Pencil, Check, X } from "lucide-react";

const SCORE_LABELS = { readiness: "Readiness", sharpness: "Sharpness", effort: "Effort+", rest: "Rest+" };

/** Population & Target · each score gauged against a coach-set target and the live squad average. */
export default function PopulationTarget({ athleteId }) {
  const { user } = useAuth();
  const isStaff = user?.role !== "athlete";
  const [pt, setPt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/athletes/${athleteId}/population-target`).then((r) => setPt(r.data)).catch(() => {});
  useEffect(() => { if (athleteId) load(); /* eslint-disable-next-line */ }, [athleteId]);

  const startEdit = () => {
    const d = {}; (pt?.scores || []).forEach((s) => (d[s.key] = s.target));
    setDraft(d); setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/athletes/${athleteId}/score-targets`, { targets: draft });
      toast.success("Targets updated"); setEditing(false); load();
    } catch (e) { toast.error("Failed to save targets"); }
    finally { setSaving(false); }
  };

  if (!pt) return null;
  return (
    <div className="border border-border p-6 mb-6" data-testid="population-target">
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <Target className="w-4 h-4" />
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Population &amp; Target</p>
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> squad of {pt.cohort_n}</span>
        {isStaff && !editing && (
          <button data-testid="edit-targets-btn" onClick={startEdit} className="ml-auto text-xs flex items-center gap-1 border border-border px-2 py-1 hover:bg-surface transition-colors"><Pencil className="w-3 h-3" /> Edit targets</button>
        )}
        {editing && (
          <span className="ml-auto flex gap-2">
            <button data-testid="save-targets-btn" onClick={save} disabled={saving} className="text-xs flex items-center gap-1 bg-foreground text-background px-2 py-1 disabled:opacity-40"><Check className="w-3 h-3" /> {saving ? "Saving…" : "Save"}</button>
            <button onClick={() => setEditing(false)} className="text-xs flex items-center gap-1 border border-border px-2 py-1"><X className="w-3 h-3" /> Cancel</button>
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">How this athlete sits against your <strong className="text-foreground">target</strong> (the goal) and the <strong className="text-foreground">squad average</strong> (everyone in the app). Not medical advice.</p>

      <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
        {pt.scores.map((s) => {
          const hit = s.value >= s.target;
          return (
            <div key={s.key} data-testid={`pt-score-${s.key}`}>
              <div className="flex items-end justify-between mb-1.5">
                <span className="text-sm font-medium">{SCORE_LABELS[s.key] || s.key}</span>
                <span className={`font-heading text-2xl font-black tabular-nums ${hit ? "text-traffic-green" : ""}`}>{s.value}</span>
              </div>
              <div className="relative h-2.5 bg-border">
                <div className={`absolute top-0 left-0 h-full ${hit ? "bg-traffic-green" : "bg-foreground"}`} style={{ width: `${Math.max(0, Math.min(100, s.value))}%` }} />
                <div className="absolute top-[-3px] h-[16px] w-0.5 bg-brand-magenta" style={{ left: `${Math.min(100, s.target)}%` }} title={`Target ${s.target}`} data-testid={`pt-target-marker-${s.key}`} />
                <div className="absolute top-[-3px] h-[16px] w-0.5 bg-muted-foreground/70" style={{ left: `${Math.min(100, s.population)}%` }} title={`Squad avg ${s.population}`} />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-0.5 bg-brand-magenta" /> Target{" "}
                  {editing ? (
                    <input data-testid={`target-input-${s.key}`} type="number" min="0" max="100" value={draft[s.key] ?? s.target}
                      onChange={(e) => setDraft((d) => ({ ...d, [s.key]: Number(e.target.value) }))}
                      className="w-14 bg-background border border-border px-1 py-0.5 text-xs tabular-nums" />
                  ) : <strong className="text-foreground tabular-nums">{s.target}</strong>}
                </span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-0.5 bg-muted-foreground/70" /> Squad avg <strong className="text-foreground tabular-nums">{s.population}</strong></span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-4">{pt.disclaimer}</p>
    </div>
  );
}
